import { useQuery } from '@apollo/client';
import type { ApolloError } from 'apollo-client';
import gql from 'graphql-tag';

import useCourseForCertificate, {
  type CertificateCourseData,
} from 'bundles/accomplishments/components/course-certificates/hooks/useCourseForCertificate';
import type { CourseCertificate } from 'bundles/accomplishments/components/course-certificates/types';
import { gradePercentageValue } from 'bundles/course-v2/utils/gradePercentageValue';

// Keep the membership data query using REST for now
export const CertificateQuery = gql`
  query CertificateQuery($code: String!) {
    MembershipsV1Resource(code: $code)
      @rest(
        type: "MembershipsV1Resource"
        path: "memberships.v1/?q=byCode&code={args.code}&showHidden=true&fields=courseId,role,vcMembershipId,vcMemberships.v1(certificateCode,grade,grantedAt),signatureTrackProfiles.v1(firstName,lastName,middleName),instructors.v1(fullName)&includes=courseId,vcMembershipId,signatureTrackProfile"
        method: "GET"
      ) {
      elements @type(name: "AccomplishmentsMembership") {
        id
        userId
        courseId
      }
      linked @type(name: "LinkedAccomplishmentData") {
        signatureTrackProfilesV1 @type(name: "AccomplishmentsSignatureTrackProfile") {
          firstName
          lastName
          middleName
        }
        vcMembershipsV1 @type(name: "AccomplishmentsVCMembership") {
          certificateCode
          grade @type(name: "AccomplishmentsVCGrade") {
            distinctionLevel
            score
          }
          grantedAt
        }
      }
    }
  }
`;

type MembershipResponse = {
  id: string;
  userId: number;
  courseId: string;
};

type SignatureTrackProfilesResponse = {
  firstName: string;
  middleName: string;
  lastName: string;
};

type VCMembershipsResponse = {
  certificateCode: string;
  grade?: {
    distinctionLevel: string;
    score: number;
  };
  grantedAt: number;
};

type CertificateDataResponse = {
  MembershipsV1Resource: {
    elements: MembershipResponse[];
    linked: {
      signatureTrackProfilesV1: SignatureTrackProfilesResponse[];
      vcMembershipsV1: VCMembershipsResponse[];
    };
  };
};

// This function combines the course data and the membership data to create the certificate data
const formatResponse = (
  membershipData: CertificateDataResponse,
  courseData: CertificateCourseData
): CourseCertificate => {
  const membershipsResponse = membershipData?.MembershipsV1Resource;
  const membershipsCoreResponse = membershipsResponse?.elements;
  const signatureTrackProfilesResponse = membershipsResponse?.linked.signatureTrackProfilesV1;
  const vcMembershipsResponse = membershipsResponse?.linked.vcMembershipsV1;

  const certificateDataResponse = {
    courseId: courseData.id,
    courseName: courseData.name,
    courseSlug: courseData.slug,
    courseDescription: courseData.description,
    courseWorkload: courseData.workload,
    // @ts-expect-error TS2532 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
    userId: membershipsCoreResponse[0].userId,
    userName: [
      // @ts-expect-error TS2532 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
      signatureTrackProfilesResponse[0].firstName,
      // @ts-expect-error TS2532 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
      signatureTrackProfilesResponse[0].middleName,
      // @ts-expect-error TS2532 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
      signatureTrackProfilesResponse[0].lastName,
    ]
      .join(' ')
      .replace(/\s+/, ' '),
    primaryPartnerLogo: courseData.partnerLogo,
    partnerNames: courseData.partnerNames,
    // @ts-expect-error TS2532 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
    certificateGrade: vcMembershipsResponse[0].grade
      ? // @ts-expect-error TS2532 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
        gradePercentageValue({ grade: vcMembershipsResponse[0].grade.score })
      : undefined,
    // @ts-expect-error TS2532 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
    certificateGrantTime: vcMembershipsResponse[0].grantedAt,
  };
  return certificateDataResponse;
};

const isEmpty = <T extends {}>(arr: T[] | undefined): boolean => {
  if (!arr) {
    return true;
  }
  return arr.length === 0;
};

type CourseCertificateData = {
  data: CourseCertificate | null;
  loading: boolean;
  error?: ApolloError | Error;
};

/**
 * Hook to fetch course certificate data using GraphQL for course details and REST for membership data
 * This replaces the old CourseCertificateDataProvider pattern
 */
const useCourseCertificateData = (certificateCode: string): CourseCertificateData => {
  // Fetch membership data (still using REST)
  const {
    data: membershipData,
    loading: membershipLoading,
    error: membershipError,
  } = useQuery<CertificateDataResponse, { code: string }>(CertificateQuery, {
    variables: { code: certificateCode },
    skip: !certificateCode,
  });

  // Extract courseId from membership data
  const courseId = membershipData?.MembershipsV1Resource?.elements?.[0]?.courseId;

  // Fetch course data using GraphQL
  const { courseData, loading: courseLoading, error: courseError } = useCourseForCertificate(courseId || '');

  const loading = membershipLoading || courseLoading;
  const error = membershipError || courseError;

  // Check if we have all required data
  const hasAllRequiredData =
    !loading &&
    !error &&
    membershipData &&
    courseData &&
    !isEmpty(membershipData?.MembershipsV1Resource?.elements) &&
    !isEmpty(membershipData?.MembershipsV1Resource?.linked?.signatureTrackProfilesV1) &&
    !isEmpty(membershipData?.MembershipsV1Resource?.linked?.vcMembershipsV1);

  const data = hasAllRequiredData ? formatResponse(membershipData, courseData) : null;

  return {
    data,
    loading,
    error,
  };
};

export default useCourseCertificateData;
